import { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnAdapter,
  DEFAULT_ADR_COL_OPTIONS,
  DEFAULT_BARCODE_COL_OPTIONS,
  DEFAULT_COLOR_COL_OPTIONS,
  DEFAULT_DATETIME_COL_OPTIONS,
  DEFAULT_FORMULA_COL_OPTIONS,
  DEFAULT_FUNCTION_COL_OPTIONS,
  DEFAULT_ICON_COL_OPTIONS,
  DEFAULT_IMG_COL_OPTIONS,
  DEFAULT_JOIN_COL_OPTIONS,
  DEFAULT_NUMBER_COL_OPTIONS,
  DEFAULT_PHONE_COL_OPTIONS,
  DEFAULT_QRCODE_COL_OPTIONS,
  DEFAULT_RATING_COL_OPTIONS,
  DEFAULT_SEO_AI_COL_OPTIONS,
  DEFAULT_SLUG_COL_OPTIONS,
  DEFAULT_TEXT_PROC_AI_COL_OPTIONS,
} from '@makyoapp/makyo-common'

import { CreateColumnProvider, useCreateColumnContext } from '@/context/CreateColumnContext'
import { CreateColumnValue } from '@/shared/fx-parser'

import { useGridV2Context } from '../../contexts/GridV2Context'
import { ColumnForm } from '../form/ColumnForm'
import { GridModalWrap } from './GridModalWrap'

interface IGridEditModal {
  column: ColumnAdapter | undefined
}

function getInitialSettingsValue(column: ColumnAdapter): CreateColumnValue {
  if (column.type === ColumnTypes.Function) {
    return { ...column.settings } as CreateColumnValue
  }

  return {
    name: column.name,
    type: column.type,
    //isUserSpecific: column.isUserSpecific,
    imageOptions: {
      imgKeywords: column.settings.imgKeywords,
      imgSourceType: column.settings.imgSourceType || DEFAULT_IMG_COL_OPTIONS.imgSourceType,
      imgSourceGravatar: column.settings.imgSourceGravatar,
      imgSourceYoutube: column.settings.imgSourceYoutube,
    },
    numberOptions: {
      numAbbreviate: column.settings?.numAbbreviate || DEFAULT_NUMBER_COL_OPTIONS.numAbbreviate,
      numDecimalSeparator: column.settings.numDecimalSeparator || DEFAULT_NUMBER_COL_OPTIONS.numDecimalSeparator,
      numThousandSeparator: column.settings.numThousandSeparator || DEFAULT_NUMBER_COL_OPTIONS.numThousandSeparator,
      numPrecision: column.settings.numPrecision || DEFAULT_NUMBER_COL_OPTIONS.numPrecision,
      numSuffix: column.settings.numSuffix || '',
      numPrefix: column.settings.numPrefix || '',
      ...(column.settings?.numberOptions || {}),
    },
    dtOptions: {
      dtDisplay: column.settings.dtDisplay ?? DEFAULT_DATETIME_COL_OPTIONS.dtDisplay,
      dtDateFormatType: column.settings.dtDateFormatType ?? DEFAULT_DATETIME_COL_OPTIONS.dtDateFormatType,
      dtLocale: column.settings.dtLocale ?? DEFAULT_DATETIME_COL_OPTIONS.dtLocale,
      dtDateFormat: column.settings.dtDateFormat ?? DEFAULT_DATETIME_COL_OPTIONS.dtDateFormat,
      dtTimeFormat: column.settings.dtTimeFormat ?? DEFAULT_DATETIME_COL_OPTIONS.dtTimeFormat,
      dtDisplaySecond: column.settings.dtDisplaySecond ?? DEFAULT_DATETIME_COL_OPTIONS.dtDisplaySecond,
      dtTimezone: column.settings.dtTimezone ?? DEFAULT_DATETIME_COL_OPTIONS.dtTimezone,
      dtDisplayTz: Boolean(column.settings.dtDisplayTz ?? DEFAULT_DATETIME_COL_OPTIONS.dtDisplayTz),
    },
    addressOptions: {
      ...DEFAULT_ADR_COL_OPTIONS,
      adrType: column.settings.adrType,
    },
    phoneOptions: {
      ...DEFAULT_PHONE_COL_OPTIONS,
      phoneFormat: column.settings.phoneFormat,
    },
    icOptions: {
      ...DEFAULT_ICON_COL_OPTIONS,
      icWeight: column.settings.icWeight,
      icFill: column.settings.icFill,
      icGrade: column.settings.icGrade,
      icOpSz: column.settings.icOpSz,
      icTy: column.settings.icTy,
      icFg: column.settings.icFg || DEFAULT_ICON_COL_OPTIONS.icFg,
      icFgCol: column.settings.icFgCol || DEFAULT_ICON_COL_OPTIONS.icFgCol,
      icBg: column.settings.icBg || DEFAULT_ICON_COL_OPTIONS.icBg,
      icBgCol: column.settings.icBgCol || DEFAULT_ICON_COL_OPTIONS.icBgCol,
    },
    ratingOptions: {
      ratingIcon: column.settings.ratingIcon || DEFAULT_RATING_COL_OPTIONS.ratingIcon,
      ratingMin: column.settings.ratingMin || DEFAULT_RATING_COL_OPTIONS.ratingMin,
      ratingMax: column.settings.ratingMax || DEFAULT_RATING_COL_OPTIONS.ratingMax,
      ratingIncrement: column.settings.ratingIncrement || DEFAULT_RATING_COL_OPTIONS.ratingIncrement,
      ratingColor: column.settings.ratingColor || DEFAULT_RATING_COL_OPTIONS.ratingColor,
      ratingColorCol: column.settings.ratingColorCol || DEFAULT_RATING_COL_OPTIONS.ratingColorCol,
    },
    slugOptions: {
      slugColumn: column.settings.slugColumn || DEFAULT_SLUG_COL_OPTIONS.slugColumn,
    },
    seoAiOptions: {
      ...DEFAULT_SEO_AI_COL_OPTIONS,
      seoAiMeta: column.settings.seoAiMeta,
      seoAiModel: column.settings.seoAiModel,
      seoAiColumn: column.settings.seoAiColumn,
    },
    textProcOptions: {
      ...DEFAULT_TEXT_PROC_AI_COL_OPTIONS,
      textProcColumn: column.settings.textProcColumn,
      textProcModel: column.settings.textProcModel,
      textProcTask: column.settings.textProcTask,
    },
    qrOptions: {
      qrType: column.settings.qrType || DEFAULT_QRCODE_COL_OPTIONS.qrType,
      qrSMS: column.settings.qrSMS || DEFAULT_QRCODE_COL_OPTIONS.qrSMS,
      qrURL: column.settings.qrURL || DEFAULT_QRCODE_COL_OPTIONS.qrURL,
      qrText: column.settings.qrText || DEFAULT_QRCODE_COL_OPTIONS.qrText,
      qrEmail: column.settings.qrEmail || DEFAULT_QRCODE_COL_OPTIONS.qrEmail,
      qrVCard: column.settings.qrVCard || DEFAULT_QRCODE_COL_OPTIONS.qrVCard,
      qrCrypto: column.settings.qrCrypto || DEFAULT_QRCODE_COL_OPTIONS.qrCrypto,
      qrFile: column.settings.qrFile || DEFAULT_QRCODE_COL_OPTIONS.qrFile,
    },
    barcodeOptions: {
      barcodeType: column.settings.barcodeType || DEFAULT_BARCODE_COL_OPTIONS.barcodeType,
      barcodeColumn: column.settings.barcodeColumn || DEFAULT_BARCODE_COL_OPTIONS.barcodeColumn,
    },
    colorOptions: {
      ...DEFAULT_COLOR_COL_OPTIONS,
      colorTransparent: column.settings.colorTransparent,
    },
    connectionOptions: {
      connFColId: column.connections?.fColId || '',
      connFColIdToShow: column.connections?.fColIdToShow || '',
      connFTableId: column.connections?.fTableId || '',
      connColId: column.connections?.colId || '',
      connMultiple: column.connections?.matchMultiple || false,
    },
    lookupOptions: {
      lookupConnColumnId: column.lookup?.connColId || '',
      lookupColumnId: column.lookup?.colId || '',
      standard: column.lookup?.standard || false,
      // numberOptions: column.settings?.numberOptions || undefined,
    },
    joinOptions: {
      joinValue: column.settings.joinValue || DEFAULT_JOIN_COL_OPTIONS.joinValue,
    },
    formulaOptions: {
      formulaValue: column.settings.formulaValue || DEFAULT_FORMULA_COL_OPTIONS.formulaValue,
      formulaPrompt: column.settings.formulaPrompt || DEFAULT_FORMULA_COL_OPTIONS.formulaPrompt,
    },
    functionOptions: {
      functionValue: column.settings.functionValue || DEFAULT_FUNCTION_COL_OPTIONS.functionValue,
    },
    mathOptions: {
      expr: column.settings.expr || '',
      vars: column.settings.vars || [],
      columns: column.settings.columns || [],
      numAbbreviate: column.settings?.numAbbreviate || DEFAULT_NUMBER_COL_OPTIONS.numAbbreviate,
      numDecimalSeparator: column.settings.numDecimalSeparator || DEFAULT_NUMBER_COL_OPTIONS.numDecimalSeparator,
      numThousandSeparator: column.settings.numThousandSeparator || DEFAULT_NUMBER_COL_OPTIONS.numThousandSeparator,
      numPrecision: column.settings.numPrecision || DEFAULT_NUMBER_COL_OPTIONS.numPrecision,
      numSuffix: column.settings.numSuffix || '',
      numPrefix: column.settings.numPrefix || '',
    },
    templateOptions: {
      tmpl: column.settings.tmpl || '',
      vars: column.settings.vars || [''],
      columns: column.settings.columns || [''],
    },
    singleCellOptions: {
      tblId: column.settings.tblId || '',
      colId: column.settings.colId || '',
      rowOptions: column.settings.rowOptions || 'first',
      row: column.settings.row || '1',
    },
  }
}

function EditInner() {
  const { reset, setIsLoading } = useCreateColumnContext()
  const { t } = useTranslation()
  const { handleCloseMenu } = useGridV2Context()

  const onHide = useCallback(() => {
    setIsLoading(false)
    reset()
    handleCloseMenu()
  }, [setIsLoading, handleCloseMenu, reset])

  const onSuccess = useCallback(
    (col?: ColumnAdapter) => {
      if (col !== undefined && [ColumnTypes.Lookup, ColumnTypes.Connection].includes(col.type)) {
        // TODO: handle list
        //handleList()
      }
      onHide()
    },
    [onHide]
  )

  return (
    <GridModalWrap handleCloseMenu={onHide} title={<Trans>Edit Column</Trans>}>
      <ColumnForm
        submitLabel={t('Save')}
        onSuccess={onSuccess}
        onHide={onHide}
        wrapClassName='w-[550px]'
        onCancel={onHide}
      />
    </GridModalWrap>
  )
}

export function GridEditModal({ column }: IGridEditModal) {
  return column ? (
    <CreateColumnProvider id={`edit-col-${column.id}`} initialValue={getInitialSettingsValue(column)}>
      <EditInner />
    </CreateColumnProvider>
  ) : null
}
